import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import API from "../../Network/API";
import toast from "react-hot-toast";
import { USER_LOGGED_IN } from "../../Reducer/authSlice";
import moment from 'moment-timezone';
import { default as Select } from "react-select";
import WaitingApproval from "./WaitingApproval";
import { useTranslation } from "react-i18next";
import "./TeacherInfo.css";

const MB = 1024 * 1024;
const PHOTO_MAX_MB = 2; // backend: image max:2048
const PHOTO_TYPES = ["image/jpeg", "image/png"];
const DOC_MAX_MB = 5; // backend: documents max:5120
const DOC_EXTENSIONS = ["pdf", "jpg", "jpeg", "png", "webp", "doc", "docx", "ppt", "pptx", "xls", "xlsx", "txt"];
const DOC_ACCEPT = DOC_EXTENSIONS.map((ext) => "." + ext).join(",");
const BIO_MIN = 20;
const BIO_MAX = 300;
const NAME_PATTERN = /^[\p{L}\p{M}\s.'-]+$/u;
const PHONE_PATTERN = /^\+?[0-9\s().-]{6,20}$/;

const STEPS = [
    { icon: "fa-light fa-user", label: "teacherInfo.about", hint: "teacherInfo.about_hint" },
    { icon: "fa-light fa-camera", label: "teacherInfo.photos", hint: "teacherInfo.photo_hint" },
    { icon: "fa-light fa-graduation-cap", label: "teacherInfo.education", hint: "teacherInfo.education_hint" },
    { icon: "fa-light fa-calendar", label: "teacherInfo.availability", hint: "teacherInfo.availability_hint" },
    { icon: "fa-light fa-wallet", label: "teacherInfo.rate_payout", hint: "teacherInfo.rate_hint" },
    { icon: "fa-light fa-file-certificate", label: "teacherInfo.documents", hint: "teacherInfo.documents_hint" },
];

const DOCUMENTS = [
    { key: "dbs_file", icon: "fa-light fa-shield-check", label: "teacherInfo.dbs", desc: "teacherInfo.dbs_desc" },
    { key: "certificate_file", icon: "fa-light fa-file-certificate", label: "teacherInfo.education_certificate", desc: "teacherInfo.certificate_desc" },
    { key: "gov_id_card", icon: "fa-light fa-id-card", label: "teacherInfo.government_id", desc: "teacherInfo.gov_id_desc" },
];

// Which step owns each field, so client and server errors can send the tutor to the right place.
const FIELD_STEP = {
    name: 1, bio: 1, country: 1, phone: 1, age_verify: 1,
    image: 2,
    education_institute: 3, education_level: 3, education_title: 3, languages: 3,
    timezone: 4, schedules: 4,
    hourly_rate: 5, bank_name: 5, account_number: 5, bank_additional: 5,
    dbs_file: 6, certificate_file: 6, gov_id_card: 6,
};
const fieldStep = (key) => FIELD_STEP[key.split(".")[0]] ?? null;

// DOM id to focus for an error key
const focusIdFor = (key) => {
    const [field, sub] = key.split(".");
    if (field === "languages") return `language-${sub ?? 0}`;
    if (field === "schedules") return sub ? `day-${sub}-from` : "day-Monday";
    if (field === "image") return "photo-input";
    return field;
};

// 30-minute time options stored as 24h "HH:mm", shown as "h:mm AM/PM"
const TIME_OPTIONS = (() => {
    const opts = [];
    for (let h = 0; h < 24; h++) {
        for (const m of [0, 30]) {
            const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
            const hour12 = h % 12 === 0 ? 12 : h % 12;
            opts.push({ value, time: `${hour12}:${String(m).padStart(2, "0")}`, pm: h >= 12 });
        }
    }
    return opts;
})();

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const WEEKDAYS = DAYS.slice(0, 5);
const DEFAULT_FROM = "09:00";
const DEFAULT_TO = "17:00";
const OFF = { from_time: "", to_time: "" };
const ON = { from_time: DEFAULT_FROM, to_time: DEFAULT_TO };
const buildSchedule = (activeDays) => Object.fromEntries(DAYS.map((d) => [d, activeDays.includes(d) ? { ...ON } : { ...OFF }]));

const DRAFT_KEY = "tutor_profile_draft";
const readDraft = () => {
    try { return JSON.parse(localStorage.getItem(DRAFT_KEY)) || {}; } catch (e) { return {}; }
};
const guessTimezone = () => {
    try { return moment.tz.guess() || null; } catch (e) { return null; }
};

const formatSize = (bytes) => bytes > MB
    ? (bytes / MB).toFixed(1) + " MB"
    : Math.max(1, Math.round(bytes / 1024)) + " KB";

function useObjectUrl(file) {
    const [url, setUrl] = useState(null);
    useEffect(() => {
        if (!file) { setUrl(null); return undefined; }
        const next = URL.createObjectURL(file);
        setUrl(next);
        return () => URL.revokeObjectURL(next);
    }, [file]);
    return url;
}

// Label + control + inline error (or hint). The message id is wired to the control's aria-describedby.
function Field({ id, label, optional, hint, error, aside, children }) {
    const { t } = useTranslation();
    return (
        <div className={`tw-field ${error ? "has-error" : ""}`}>
            {label && (
                <div className="tw-label-row">
                    <label htmlFor={id} className="tw-label">
                        {label}
                        {optional && <span className="tw-optional">({t("teacherInfo.optional").toLowerCase()})</span>}
                    </label>
                    {aside}
                </div>
            )}
            {children}
            {error ? (
                <p className="tw-error" id={`${id}-msg`} role="alert">
                    <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
                    <span>{error}</span>
                </p>
            ) : hint ? (
                <p className="tw-hint" id={`${id}-msg`}>{hint}</p>
            ) : null}
        </div>
    );
}

// Drag-and-drop target wrapping a visually hidden file input (still focusable and keyboard operable).
function useDropHandlers(onFile) {
    const [over, setOver] = useState(false);
    return {
        over,
        handlers: {
            onDragOver: (e) => { e.preventDefault(); setOver(true); },
            onDragLeave: () => setOver(false),
            onDrop: (e) => {
                e.preventDefault();
                setOver(false);
                const f = e.dataTransfer.files?.[0];
                if (f) onFile(f);
            },
        },
    };
}

function HiddenFileInput({ id, accept, onFile, invalid }) {
    return (
        <input
            id={id}
            type="file"
            accept={accept}
            className="tw-visually-hidden"
            aria-invalid={invalid || undefined}
            aria-describedby={`${id}-msg`}
            onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onFile(f);
                e.target.value = ""; // allow re-picking the same file after removing it
            }}
        />
    );
}

function PhotoDrop({ photo, photoUrl, error, onFile }) {
    const { t } = useTranslation();
    const { over, handlers } = useDropHandlers(onFile);
    return (
        <label htmlFor="photo-input" className={`tw-drop ${over ? "is-over" : ""} ${error ? "is-invalid" : ""}`} {...handlers}>
            <HiddenFileInput id="photo-input" accept=".jpg,.jpeg,.png" onFile={onFile} invalid={!!error} />
            {photoUrl ? (
                <>
                    <img src={photoUrl} alt="" className="tw-drop-photo" />
                    <span className="tw-drop-title"><span>{t("teacherInfo.change_photo")}</span></span>
                    <span className="tw-drop-sub">{photo?.name} · {formatSize(photo?.size ?? 0)}</span>
                </>
            ) : (
                <>
                    <span className="tw-drop-icon"><i className="fa-light fa-cloud-arrow-up" aria-hidden="true" /></span>
                    <span className="tw-drop-title">
                        <span>{t("teacherInfo.click_to_upload")}</span> {t("teacherInfo.or_drag_drop")}
                    </span>
                    <span className="tw-drop-sub">{t("teacherInfo.photo_format_hint", { size: PHOTO_MAX_MB })}</span>
                </>
            )}
        </label>
    );
}

function DocumentRow({ doc, file, error, onFile, onClear }) {
    const { t } = useTranslation();
    const { over, handlers } = useDropHandlers(onFile);
    return (
        <div className="tw-field">
            <div className={`tw-doc ${over ? "is-over" : ""} ${file ? "is-set" : ""} ${error ? "is-invalid" : ""}`} {...handlers}>
                <span className="tw-doc-icon"><i className={doc.icon} aria-hidden="true" /></span>
                <div className="tw-doc-body">
                    <div className="tw-doc-title">{t(doc.label)}</div>
                    {file ? (
                        <div className="tw-doc-file">
                            <i className="fa-solid fa-circle-check" aria-hidden="true" />
                            <span className="tw-doc-file-name">{file.name}</span>
                            <span className="tw-doc-file-size">{formatSize(file.size)}</span>
                        </div>
                    ) : (
                        <div className="tw-doc-desc">{t(doc.desc)}</div>
                    )}
                </div>
                <div className="tw-doc-actions">
                    <HiddenFileInput id={doc.key} accept={DOC_ACCEPT} onFile={onFile} invalid={!!error} />
                    <label htmlFor={doc.key} className="tw-btn tw-btn-ghost tw-btn-sm m-0">
                        <i className={file ? "fa-light fa-rotate-right" : "fa-light fa-cloud-arrow-up"} aria-hidden="true" />
                        {file ? t("teacherInfo.replace") : t("teacherInfo.upload")}
                    </label>
                    {file && (
                        <button type="button" className="tw-icon-btn" style={{ width: 32, height: 32 }} onClick={onClear} aria-label={t("teacherInfo.remove")}>
                            <i className="fa-light fa-xmark" aria-hidden="true" />
                        </button>
                    )}
                </div>
            </div>
            {error && (
                <p className="tw-error" id={`${doc.key}-msg`} role="alert">
                    <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
                    <span>{error}</span>
                </p>
            )}
        </div>
    );
}

export default function TeacherInfo() {
    const { t } = useTranslation();
    const { user } = useSelector((state) => state.authReducer);
    const select_lang = useSelector((state) => state.languageReducer);
    const dispatch = useDispatch();

    const [draft] = useState(readDraft);
    const [step, setStep] = useState(() => Math.min(Math.max(Number(draft.step) || 1, 1), STEPS.length));
    const [furthest, setFurthest] = useState(() => Math.max(Number(draft.furthest) || 1, Number(draft.step) || 1));
    const [input, setInput] = useState(() => ({ name: user?.name ?? "", ...(draft.input ?? {}) }));
    const [country_id, setCountryId] = useState(draft.country_id || "");
    const [time_zone, setTimeZone] = useState(() => draft.time_zone || guessTimezone());
    const [schedules, setSchedules] = useState(() => draft.schedules ?? buildSchedule(WEEKDAYS));
    const [languages, setLanguages] = useState(() => draft.languages?.length ? draft.languages : [{ language: "", lang_type: "" }]);
    const [photo, setPhoto] = useState(null);
    const [docs, setDocs] = useState({ dbs_file: null, certificate_file: null, gov_id_card: null });

    const [errors, setErrors] = useState({});
    const [banner, setBanner] = useState(null);
    const [focusTarget, setFocusTarget] = useState(null);
    const [countries, setCountries] = useState([]);
    const [countriesStatus, setCountriesStatus] = useState("loading");
    const [submitting, setSubmitting] = useState(false);
    const [uploadPct, setUploadPct] = useState(0);
    const photoUrl = useObjectUrl(photo);
    const didMount = useRef(false);

    const langTypeOptions = [
        { value: "native", label: t("teacherInfo.lang_native") },
        { value: "fluent", label: t("teacherInfo.lang_fluent") },
        { value: "conversational", label: t("teacherInfo.lang_conversational") },
        { value: "basic", label: t("teacherInfo.lang_basic") },
    ];

    const dayLabels = {
        Monday: t("teacherInfo.monday"),
        Tuesday: t("teacherInfo.tuesday"),
        Wednesday: t("teacherInfo.wednesday"),
        Thursday: t("teacherInfo.thursday"),
        Friday: t("teacherInfo.friday"),
        Saturday: t("teacherInfo.saturday"),
        Sunday: t("teacherInfo.sunday"),
    };

    const timezones = useMemo(() => moment.tz.names().map((tz) => ({
        value: tz,
        label: `(UTC${moment.tz(tz).format("Z")}) ${tz.replace(/_/g, " ")}`,
    })), []);

    // autosave draft so a refresh never loses progress (files can't be persisted)
    useEffect(() => {
        try {
            localStorage.setItem(DRAFT_KEY, JSON.stringify({ input, languages, schedules, time_zone, country_id, step, furthest }));
        } catch (e) { /* storage full or blocked: the form still works, it just won't survive a refresh */ }
    }, [input, languages, schedules, time_zone, country_id, step, furthest]);

    useEffect(() => {
        if ((Number(draft.step) || 1) > 1 || Object.keys(draft.input ?? {}).length > 0) {
            const lostFiles = (Number(draft.furthest) || Number(draft.step) || 1) > 2;
            toast(t(lostFiles ? "teacherInfo.welcome_back_files" : "teacherInfo.welcome_back_restored"), { icon: "💾", duration: 6000 });
        }
        loadCountries();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!didMount.current) { didMount.current = true; return; }
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [step]);

    // runs after the step containing the field has rendered
    useEffect(() => {
        if (!focusTarget) return;
        const el = document.getElementById(focusTarget.id);
        if (!el) return;
        el.focus({ preventScroll: true });
        (el.closest(".tw-field") || el).scrollIntoView({ behavior: "smooth", block: "center" });
    }, [focusTarget]);

    const focusField = (key) => setFocusTarget({ id: focusIdFor(key), at: Date.now() });

    const loadCountries = () => {
        setCountriesStatus("loading");
        API.get(`/v1/oauth/countries`).then((response) => {
            setCountries((response.data?.data?.countries ?? []).map((item) => ({ value: item.code, label: item.name })));
            setCountriesStatus("ready");
        }).catch(() => {
            setCountriesStatus("error");
        });
    };

    const clearError = (...keys) => {
        setErrors((prev) => {
            if (!keys.some((k) => k in prev)) return prev;
            const next = { ...prev };
            keys.forEach((k) => delete next[k]);
            return next;
        });
    };

    const setFieldError = (key, message) => setErrors((prev) => ({ ...prev, [key]: message }));

    const handleChange = (e) => {
        const { name, type } = e.target;
        const value = type === "checkbox" ? e.target.checked : e.target.value;
        setInput((prev) => ({ ...prev, [name]: value }));
        clearError(name);
    };

    // ---------- validation ----------
    const validateStep = (s) => {
        const e = {};
        if (s === 1) {
            const name = String(input.name ?? "").trim();
            if (!name) e.name = t("teacherInfo.enter_name");
            else if (name.length < 2 || !NAME_PATTERN.test(name)) e.name = t("teacherInfo.name_invalid");
            const bio = String(input.bio ?? "").trim();
            if (!bio) e.bio = t("teacherInfo.enter_bio");
            else if (bio.length < BIO_MIN) e.bio = t("teacherInfo.bio_too_short", { min: BIO_MIN, len: bio.length });
            if (!country_id) e.country = t("teacherInfo.select_country");
            const phone = String(input.phone ?? "").trim();
            if (phone && !PHONE_PATTERN.test(phone)) e.phone = t("teacherInfo.phone_invalid");
            if (!input.age_verify) e.age_verify = t("teacherInfo.confirm_over_18");
        }
        if (s === 2 && !photo) {
            e.image = t("teacherInfo.upload_profile_photo");
        }
        if (s === 3) {
            if (!String(input.education_institute ?? "").trim()) e.education_institute = t("teacherInfo.enter_university");
            if (!String(input.education_level ?? "").trim()) e.education_level = t("teacherInfo.enter_degree");
            if (!String(input.education_title ?? "").trim()) e.education_title = t("teacherInfo.enter_education_title");
            languages.forEach((l, i) => {
                if (l.language && !l.lang_type) e[`languages.${i}`] = t("teacherInfo.pick_level");
                else if (!l.language && l.lang_type) e[`languages.${i}`] = t("teacherInfo.pick_language");
            });
            if (!languages.some((l) => l.language && l.lang_type) && !Object.keys(e).some((k) => k.startsWith("languages."))) {
                e["languages.0"] = t("teacherInfo.add_language_level");
            }
        }
        if (s === 4) {
            if (!time_zone) e.timezone = t("teacherInfo.choose_timezone");
            const active = DAYS.filter((d) => schedules[d]?.from_time && schedules[d]?.to_time);
            if (!active.length) e.schedules = t("teacherInfo.set_availability_day");
            active.forEach((d) => {
                if (schedules[d].from_time >= schedules[d].to_time) e[`schedules.${d}`] = t("teacherInfo.end_after_start");
            });
        }
        if (s === 5) {
            const rate = String(input.hourly_rate ?? "").trim();
            if (rate === "") e.hourly_rate = t("teacherInfo.enter_hourly_rate");
            else if (!(Number(rate) > 0)) e.hourly_rate = t("teacherInfo.hourly_rate_positive");
            // payout details are optional (can be set later in Settings), but if one is started the pair must be complete
            const bank = String(input.bank_name ?? "").trim();
            const acct = String(input.account_number ?? "").trim();
            if (acct && !bank) e.bank_name = t("teacherInfo.bank_name_required");
            if (bank && !acct) e.account_number = t("teacherInfo.account_number_required");
        }
        if (s === 6) {
            DOCUMENTS.forEach((d) => { if (!docs[d.key]) e[d.key] = t("teacherInfo.document_required"); });
        }
        return e;
    };

    // replace the errors owned by step `s` with a fresh validation result
    const applyStepErrors = (s, stepErrors) => {
        setErrors((prev) => ({
            ...Object.fromEntries(Object.entries(prev).filter(([k]) => fieldStep(k) !== s)),
            ...stepErrors,
        }));
    };

    const goNext = () => {
        const e = validateStep(step);
        applyStepErrors(step, e);
        const keys = Object.keys(e);
        if (keys.length) {
            focusField(keys[0]);
            return;
        }
        setBanner(null);
        const next = Math.min(step + 1, STEPS.length);
        setStep(next);
        setFurthest((f) => Math.max(f, next));
    };

    const goBack = () => {
        setBanner(null);
        setStep((s) => Math.max(1, s - 1));
    };

    const goToStep = (s) => {
        if (s === step || s > furthest) return;
        setBanner(null);
        setStep(s);
    };

    // ---------- field handlers ----------
    const pickPhoto = (f) => {
        if (!PHOTO_TYPES.includes(f.type)) return setFieldError("image", t("teacherInfo.photo_type_invalid"));
        if (f.size > PHOTO_MAX_MB * MB) return setFieldError("image", t("teacherInfo.photo_too_large", { size: PHOTO_MAX_MB, actual: formatSize(f.size) }));
        setPhoto(f);
        clearError("image");
    };

    const pickDocument = (key, f) => {
        const ext = (f.name.split(".").pop() || "").toLowerCase();
        if (!DOC_EXTENSIONS.includes(ext)) return setFieldError(key, t("teacherInfo.document_type_invalid"));
        if (f.size > DOC_MAX_MB * MB) return setFieldError(key, t("teacherInfo.document_too_large", { size: DOC_MAX_MB, actual: formatSize(f.size) }));
        setDocs((prev) => ({ ...prev, [key]: f }));
        clearError(key);
    };

    const updateLanguage = (index, field, value) => {
        setLanguages((prev) => prev.map((l, i) => (i === index ? { ...l, [field]: value } : l)));
        clearError(`languages.${index}`);
    };

    const addLanguage = () => setLanguages((prev) => [...prev, { language: "", lang_type: "" }]);

    const removeLanguage = (index) => {
        setLanguages((prev) => prev.filter((_, i) => i !== index));
        // row errors are index-based; re-validate on the next "Continue" instead of shifting them
        setErrors((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => !k.startsWith("languages."))));
    };

    const updateDay = (day, field, value) => {
        setSchedules((prev) => ({ ...prev, [day]: { ...prev[day], [field]: value } }));
        clearError(`schedules.${day}`);
    };

    const toggleDay = (day, on) => {
        setSchedules((prev) => ({ ...prev, [day]: on ? { ...ON } : { ...OFF } }));
        clearError("schedules", `schedules.${day}`);
    };

    const applyPreset = (activeDays) => {
        setSchedules(buildSchedule(activeDays));
        setErrors((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => !k.startsWith("schedules"))));
    };

    // ---------- submit ----------
    const handleServerError = (err) => {
        const res = err?.response;
        if (!res) {
            setBanner({ tone: "danger", icon: "fa-wifi-slash", title: t("teacherInfo.err_network_title"), message: t("teacherInfo.err_network"), retry: true });
            setFocusTarget({ id: "tw-banner", at: Date.now() });
            return;
        }
        if (res.status === 401) {
            setBanner({ tone: "danger", icon: "fa-lock", title: t("teacherInfo.err_session_title"), message: t("teacherInfo.err_session") });
            setFocusTarget({ id: "tw-banner", at: Date.now() });
            return;
        }
        if (res.status === 413) {
            setBanner({ tone: "danger", icon: "fa-file-exclamation", title: t("teacherInfo.err_too_large_title"), message: t("teacherInfo.err_too_large", { size: DOC_MAX_MB }) });
            setFocusTarget({ id: "tw-banner", at: Date.now() });
            return;
        }

        const serverErrors = res.data?.errors;
        if (serverErrors && typeof serverErrors === "object" && !Array.isArray(serverErrors) && Object.keys(serverErrors).length) {
            // Laravel validation errors: show each next to its field and jump to the earliest step that has one
            const mapped = {};
            const unmapped = [];
            Object.entries(serverErrors).forEach(([key, msgs]) => {
                const message = Array.isArray(msgs) ? msgs[0] : String(msgs);
                const field = key.split(".")[0];
                if (fieldStep(field)) {
                    if (!mapped[field]) mapped[field] = message;
                } else {
                    unmapped.push(message);
                }
            });
            setErrors(mapped);
            const firstKey = Object.keys(mapped).sort((a, b) => fieldStep(a) - fieldStep(b))[0];
            const target = firstKey ? fieldStep(firstKey) : step;
            setBanner({
                tone: "danger",
                icon: "fa-circle-exclamation",
                title: t("teacherInfo.err_validation_title"),
                message: t("teacherInfo.err_validation"),
                items: unmapped,
            });
            setStep(target);
            if (firstKey) focusField(firstKey);
            return;
        }

        setBanner({
            tone: "danger",
            icon: "fa-triangle-exclamation",
            title: t("teacherInfo.err_server_title"),
            message: res.data?.message && res.status < 500 ? res.data.message : t("teacherInfo.err_server"),
            retry: true,
        });
        setFocusTarget({ id: "tw-banner", at: Date.now() });
    };

    const submit = () => {
        if (submitting) return;

        // re-check every step: a restored draft can reach this point with stale fields or without files
        const all = {};
        for (let s = 1; s <= STEPS.length; s++) Object.assign(all, validateStep(s));
        setErrors(all);
        const keys = Object.keys(all);
        if (keys.length) {
            const first = fieldStep(keys[0]);
            if (first !== step) {
                setBanner({ tone: "warning", icon: "fa-circle-exclamation", title: t("teacherInfo.fix_before_submit_title"), message: t("teacherInfo.fix_before_submit") });
                setStep(first);
            }
            focusField(keys[0]);
            return;
        }

        setBanner(null);
        setSubmitting(true);
        setUploadPct(0);

        const fd = new FormData();
        fd.append("name", String(input.name).trim());
        fd.append("bio", String(input.bio).trim());
        fd.append("country", country_id);
        fd.append("phone", String(input.phone ?? "").trim());
        fd.append("image", photo, photo.name);
        DOCUMENTS.forEach((d) => fd.append(d.key, docs[d.key], docs[d.key].name));
        fd.append("age_verify", input.age_verify ? 1 : 0);
        fd.append("check_eduction", input.check_eduction ? 1 : 0);
        fd.append("education_institute", String(input.education_institute).trim());
        fd.append("education_level", String(input.education_level).trim());
        fd.append("education_title", String(input.education_title).trim());
        fd.append("hourly_rate", String(input.hourly_rate).trim());
        fd.append("bank_name", String(input.bank_name ?? "").trim());
        fd.append("account_number", String(input.account_number ?? "").trim());
        fd.append("bank_additional", String(input.bank_additional ?? "").trim());
        fd.append("timezone", time_zone);
        fd.append("schedules", JSON.stringify(schedules));
        fd.append("languages", JSON.stringify(languages.filter((l) => l.language && l.lang_type)));

        API.post("/v1/oauth/settings/tutor_profile", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (e) => {
                if (e.total) setUploadPct(Math.round((e.loaded * 100) / e.total));
            },
        }).then((response) => {
            const updatedUser = response?.data?.data?.user;
            if (!updatedUser) throw Object.assign(new Error("Malformed response"), { response: { status: 500, data: {} } });
            const auth = { user: updatedUser, accessToken: localStorage.getItem("xller_token") };
            localStorage.setItem("xller_auth", JSON.stringify(auth));
            localStorage.removeItem(DRAFT_KEY);
            dispatch(USER_LOGGED_IN(auth));
            toast.success(t("teacherInfo.profile_submitted"));
            window.scrollTo(0, 0);
        }).catch(handleServerError).finally(() => {
            setSubmitting(false);
        });
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        // Enter in a text field should move forward, not submit from an earlier step
        if (step < STEPS.length) goNext();
        else submit();
    };

    if (user?.approved == 0 && user?.teacher_info != '') {
        return <WaitingApproval />;
    }

    // ---------- render helpers ----------
    const err = (key) => errors[key];
    const invalidProps = (key) => ({
        "aria-invalid": err(key) ? true : undefined,
        "aria-describedby": `${focusIdFor(key)}-msg`,
    });
    const stepHasError = (s) => Object.keys(errors).some((k) => fieldStep(k) === s);
    const countryLabel = countries.find((c) => c.value === country_id)?.label;
    const bioLength = String(input.bio ?? "").length;
    const chosenLanguages = languages.map((l) => String(l.language)).filter(Boolean);
    const current = STEPS[step - 1];
    const progress = Math.round((step / STEPS.length) * 100);

    return (
        <div className="tw-page">
            <div className="container">
                <div className="tw-layout">
                    {/* ---------- sidebar stepper ---------- */}
                    <aside className="tw-aside">
                        <div className="tw-eyebrow"><i className="fa-solid fa-chalkboard-user" aria-hidden="true" /> {t("teacherInfo.application_eyebrow")}</div>
                        <h1>{t("teacherInfo.wizard_title")}</h1>
                        <p className="tw-aside-lead">{t("teacherInfo.wizard_lead")}</p>

                        <div className="tw-progress">
                            <div className="tw-progress-meta">
                                <span>{t("teacherInfo.step_of", { current: step, total: STEPS.length })}</span>
                                <span>{progress}%</span>
                            </div>
                            <div className="tw-progress-track" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}>
                                <div className="tw-progress-fill" style={{ width: `${progress}%` }} />
                            </div>
                        </div>

                        <ol className="tw-steps">
                            {STEPS.map((s, i) => {
                                const n = i + 1;
                                const isCurrent = n === step;
                                const hasError = stepHasError(n);
                                const isDone = !isCurrent && n < furthest && !hasError;
                                const state = isCurrent ? "is-current" : isDone ? "is-done" : n <= furthest ? "is-visited" : "is-upcoming";
                                return (
                                    <li key={s.label} className={`tw-step ${state} ${hasError ? "has-error" : ""}`}>
                                        <button
                                            type="button"
                                            className="tw-step-btn"
                                            onClick={() => goToStep(n)}
                                            disabled={isCurrent || n > furthest}
                                            aria-current={isCurrent ? "step" : undefined}
                                            aria-label={`${t(s.label)}${hasError ? ` — ${t("teacherInfo.needs_attention")}` : isDone ? ` — ${t("teacherInfo.completed")}` : ""}`}
                                        >
                                            <span className="tw-step-dot">
                                                <i className={hasError && !isCurrent ? "fa-solid fa-exclamation" : isDone ? "fa-solid fa-check" : s.icon} aria-hidden="true" />
                                            </span>
                                            <span className="tw-step-text">
                                                <span className="tw-step-label">{t(s.label)}</span>
                                                <span className="tw-step-hint">{hasError ? t("teacherInfo.needs_attention") : t(s.hint)}</span>
                                            </span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ol>

                        <div className="tw-saved">
                            <i className="fa-light fa-circle-check" aria-hidden="true" />
                            {t("teacherInfo.saved_on_device")}
                        </div>
                    </aside>

                    {/* ---------- step card ---------- */}
                    <form className="tw tw-card" onSubmit={handleSubmit} noValidate>
                        <div className="tw-card-body">
                            <div className="tw-card-kicker">{t("teacherInfo.step_of", { current: step, total: STEPS.length })}</div>
                            <h2 className="tw-card-title">{t(current.label)}</h2>

                            {banner && (
                                <div id="tw-banner" tabIndex={-1} className={`tw-banner is-${banner.tone}`} role="alert">
                                    <i className={`fa-light ${banner.icon}`} aria-hidden="true" />
                                    <div>
                                        <p className="tw-banner-title">{banner.title}</p>
                                        <p>{banner.message}</p>
                                        {banner.items?.length > 0 && (
                                            <ul>{banner.items.map((m, i) => <li key={i}>{m}</li>)}</ul>
                                        )}
                                        {banner.retry && step === STEPS.length && (
                                            <div className="tw-banner-actions">
                                                <button type="button" className="tw-btn tw-btn-ghost tw-btn-sm" onClick={submit} disabled={submitting}>
                                                    <i className="fa-light fa-rotate-right" aria-hidden="true" /> {t("teacherInfo.try_again")}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    <button type="button" className="tw-banner-close" onClick={() => setBanner(null)} aria-label={t("teacherInfo.dismiss")}>
                                        <i className="fa-light fa-xmark" aria-hidden="true" />
                                    </button>
                                </div>
                            )}

                            {step === 1 && (
                                <>
                                    <p className="tw-card-desc">{t("teacherInfo.about_desc")}</p>

                                    <Field id="name" label={t("teacherInfo.your_name")} error={err("name")} hint={t("teacherInfo.name_hint")}>
                                        <input
                                            id="name" name="name" type="text" autoComplete="name"
                                            className={`form-control ${err("name") ? "is-invalid" : ""}`}
                                            value={input.name ?? ""}
                                            onChange={handleChange}
                                            placeholder={t("teacherInfo.your_full_name")}
                                            maxLength={255}
                                            {...invalidProps("name")}
                                        />
                                    </Field>

                                    <Field
                                        id="bio" label={t("teacherInfo.bio")} error={err("bio")} hint={t("teacherInfo.bio_hint", { min: BIO_MIN })}
                                        aside={<span className={`tw-counter ${bioLength > BIO_MAX - 20 ? "is-low" : ""}`}>{bioLength}/{BIO_MAX}</span>}
                                    >
                                        <textarea
                                            id="bio" name="bio" rows={3}
                                            className={`form-control ${err("bio") ? "is-invalid" : ""}`}
                                            value={input.bio ?? ""}
                                            onChange={handleChange}
                                            placeholder={t("teacherInfo.bio_placeholder")}
                                            maxLength={BIO_MAX}
                                            {...invalidProps("bio")}
                                        />
                                    </Field>

                                    <Field
                                        id="country" label={t("teacherInfo.country")} error={err("country")}
                                        hint={countriesStatus === "error" ? (
                                            <span>
                                                {t("teacherInfo.countries_failed")}{" "}
                                                <button type="button" className="tw-link-btn" onClick={loadCountries}>{t("teacherInfo.try_again")}</button>
                                            </span>
                                        ) : null}
                                    >
                                        <Select
                                            inputId="country"
                                            className={`tw-select ${err("country") ? "is-invalid" : ""}`}
                                            classNamePrefix="tw-select"
                                            options={countries}
                                            isLoading={countriesStatus === "loading"}
                                            isClearable
                                            value={countries.find((c) => c.value === country_id) || null}
                                            onChange={(opt) => { setCountryId(opt?.value ?? ""); clearError("country"); }}
                                            placeholder={t("teacherInfo.select_country_placeholder")}
                                            noOptionsMessage={() => countriesStatus === "error" ? t("teacherInfo.countries_failed") : t("teacherInfo.no_matches")}
                                            aria-invalid={err("country") ? true : undefined}
                                            aria-describedby="country-msg"
                                        />
                                    </Field>

                                    <Field id="phone" label={t("teacherInfo.phone")} optional error={err("phone")} hint={t("teacherInfo.phone_hint")}>
                                        <input
                                            id="phone" name="phone" type="tel" autoComplete="tel" inputMode="tel"
                                            className={`form-control ${err("phone") ? "is-invalid" : ""}`}
                                            value={input.phone ?? ""}
                                            onChange={handleChange}
                                            placeholder="+44 7700 900123"
                                            maxLength={30}
                                            {...invalidProps("phone")}
                                        />
                                    </Field>

                                    <Field id="age_verify" error={err("age_verify")}>
                                        <label className="tw-check" htmlFor="age_verify">
                                            <input
                                                id="age_verify" name="age_verify" type="checkbox"
                                                className="form-check-input"
                                                checked={!!input.age_verify}
                                                onChange={handleChange}
                                                {...invalidProps("age_verify")}
                                            />
                                            <span className="tw-check-text">{t("teacherInfo.confirm_over_18_label")}</span>
                                        </label>
                                    </Field>
                                </>
                            )}

                            {step === 2 && (
                                <>
                                    <p className="tw-card-desc">{t("teacherInfo.profile_photo_desc")}</p>
                                    <div className="tw-photo-grid">
                                        <Field id="photo-input" error={err("image")}>
                                            <PhotoDrop photo={photo} photoUrl={photoUrl} error={err("image")} onFile={pickPhoto} />
                                        </Field>
                                        <div>
                                            <div className="tw-preview-label">{t("teacherInfo.preview_label")}</div>
                                            <div className="tw-preview">
                                                <div className="tw-preview-head">
                                                    {photoUrl
                                                        ? <img src={photoUrl} alt="" className="tw-preview-avatar" />
                                                        : <span className="tw-preview-avatar"><i className="fa-light fa-user" aria-hidden="true" /></span>}
                                                    <div>
                                                        <div className="tw-preview-name">{String(input.name ?? "").trim() || t("teacherInfo.your_name")}</div>
                                                        {countryLabel && <div className="tw-preview-meta"><i className="fa-light fa-globe me-1" aria-hidden="true" />{countryLabel}</div>}
                                                    </div>
                                                </div>
                                                <p className="tw-preview-bio">{String(input.bio ?? "").trim() || t("teacherInfo.bio_placeholder")}</p>
                                            </div>
                                            <ul className="tw-tips">
                                                <li><i className="fa-solid fa-check" aria-hidden="true" />{t("teacherInfo.photo_tip_face")}</li>
                                                <li><i className="fa-solid fa-check" aria-hidden="true" />{t("teacherInfo.photo_tip_light")}</li>
                                                <li><i className="fa-solid fa-check" aria-hidden="true" />{t("teacherInfo.photo_tip_solo")}</li>
                                            </ul>
                                        </div>
                                    </div>
                                </>
                            )}

                            {step === 3 && (
                                <>
                                    <p className="tw-card-desc">{t("teacherInfo.education_desc")}</p>

                                    <Field id="education_institute" label={t("teacherInfo.university")} error={err("education_institute")}>
                                        <input
                                            id="education_institute" name="education_institute" type="text" autoComplete="organization"
                                            className={`form-control ${err("education_institute") ? "is-invalid" : ""}`}
                                            value={input.education_institute ?? ""}
                                            onChange={handleChange}
                                            placeholder={t("teacherInfo.university_placeholder")}
                                            maxLength={255}
                                            {...invalidProps("education_institute")}
                                        />
                                    </Field>
                                    <Field id="education_level" label={t("teacherInfo.degree")} error={err("education_level")}>
                                        <input
                                            id="education_level" name="education_level" type="text"
                                            className={`form-control ${err("education_level") ? "is-invalid" : ""}`}
                                            value={input.education_level ?? ""}
                                            onChange={handleChange}
                                            placeholder={t("teacherInfo.degree_placeholder")}
                                            maxLength={255}
                                            {...invalidProps("education_level")}
                                        />
                                    </Field>
                                    <Field id="education_title" label={t("teacherInfo.education_title")} error={err("education_title")} hint={t("teacherInfo.education_title_hint")}>
                                        <input
                                            id="education_title" name="education_title" type="text"
                                            className={`form-control ${err("education_title") ? "is-invalid" : ""}`}
                                            value={input.education_title ?? ""}
                                            onChange={handleChange}
                                            placeholder={t("teacherInfo.education_title_placeholder")}
                                            maxLength={255}
                                            {...invalidProps("education_title")}
                                        />
                                    </Field>

                                    <h3 className="tw-section-title"><i className="fa-light fa-language" aria-hidden="true" /> {t("teacherInfo.languages_you_speak")}</h3>
                                    <p className="tw-section-desc">{t("teacherInfo.languages_desc")}</p>

                                    {languages.map((lang, index) => (
                                        <Field key={index} id={`language-${index}`} error={err(`languages.${index}`)}>
                                            <div className="tw-lang-row">
                                                <select
                                                    id={`language-${index}`}
                                                    className={`form-select ${err(`languages.${index}`) && !lang.language ? "is-invalid" : ""}`}
                                                    value={String(lang.language ?? "")}
                                                    onChange={(e) => updateLanguage(index, "language", e.target.value)}
                                                    aria-label={t("teacherInfo.language")}
                                                    {...(err(`languages.${index}`) ? { "aria-invalid": true } : {})}
                                                    aria-describedby={`language-${index}-msg`}
                                                >
                                                    <option value="">{t("teacherInfo.select_language")}</option>
                                                    {(select_lang?.data ?? []).map((option) => {
                                                        const id = String(option.id);
                                                        return (
                                                            <option key={id} value={id} disabled={chosenLanguages.includes(id) && id !== String(lang.language)}>
                                                                {option.name}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                                <select
                                                    className={`form-select ${err(`languages.${index}`) && lang.language && !lang.lang_type ? "is-invalid" : ""}`}
                                                    value={lang.lang_type ?? ""}
                                                    onChange={(e) => updateLanguage(index, "lang_type", e.target.value)}
                                                    aria-label={t("teacherInfo.level")}
                                                >
                                                    <option value="">{t("teacherInfo.select_level")}</option>
                                                    {langTypeOptions.map((option) => (
                                                        <option key={option.value} value={option.value}>{option.label}</option>
                                                    ))}
                                                </select>
                                                <button
                                                    type="button" className="tw-icon-btn"
                                                    onClick={() => removeLanguage(index)}
                                                    disabled={languages.length === 1}
                                                    aria-label={t("teacherInfo.remove_language")}
                                                >
                                                    <i className="fa-light fa-trash-can" aria-hidden="true" />
                                                </button>
                                            </div>
                                        </Field>
                                    ))}
                                    <button type="button" className="tw-add-btn" onClick={addLanguage}>
                                        <i className="fa-light fa-plus" aria-hidden="true" /> {t("teacherInfo.add_language")}
                                    </button>
                                </>
                            )}

                            {step === 4 && (
                                <>
                                    <p className="tw-card-desc">{t("teacherInfo.availability_desc")}</p>

                                    <Field id="timezone" label={t("teacherInfo.choose_your_timezone")} error={err("timezone")} hint={t("teacherInfo.timezone_hint")}>
                                        <Select
                                            inputId="timezone"
                                            className={`tw-select ${err("timezone") ? "is-invalid" : ""}`}
                                            classNamePrefix="tw-select"
                                            options={timezones}
                                            isClearable
                                            value={timezones.find((o) => o.value === time_zone) || null}
                                            onChange={(opt) => { setTimeZone(opt?.value ?? null); clearError("timezone"); }}
                                            placeholder={t("teacherInfo.select_timezone_placeholder")}
                                            noOptionsMessage={() => t("teacherInfo.no_matches")}
                                            aria-describedby="timezone-msg"
                                        />
                                    </Field>

                                    <div className="tw-label-row">
                                        <span className="tw-label">{t("teacherInfo.weekly_hours")}</span>
                                    </div>
                                    <div className="tw-presets">
                                        <button type="button" className="tw-btn tw-btn-ghost tw-btn-sm" onClick={() => applyPreset(WEEKDAYS)}>{t("teacherInfo.preset_weekdays")}</button>
                                        <button type="button" className="tw-btn tw-btn-ghost tw-btn-sm" onClick={() => applyPreset(DAYS)}>{t("teacherInfo.preset_every_day")}</button>
                                        <button type="button" className="tw-btn tw-btn-ghost tw-btn-sm" onClick={() => applyPreset([])}>{t("teacherInfo.preset_clear")}</button>
                                    </div>

                                    <Field id="day-Monday" error={err("schedules")}>
                                        {DAYS.map((day) => {
                                            const d = schedules[day] ?? OFF;
                                            const active = !!(d.from_time && d.to_time);
                                            const invalid = active && d.from_time >= d.to_time;
                                            return (
                                                <div key={day} className={`tw-day ${active ? "is-active" : ""} ${invalid ? "is-invalid" : ""}`}>
                                                    <div className="form-check form-switch tw-day-toggle">
                                                        <input
                                                            className="form-check-input" type="checkbox" role="switch"
                                                            id={`day-${day}`}
                                                            checked={active}
                                                            onChange={(e) => toggleDay(day, e.target.checked)}
                                                        />
                                                        <label className="form-check-label" htmlFor={`day-${day}`}>{dayLabels[day]}</label>
                                                    </div>
                                                    {active ? (
                                                        <div className="tw-day-times">
                                                            <select
                                                                id={`day-${day}-from`}
                                                                className={`form-select ${invalid ? "is-invalid" : ""}`}
                                                                value={d.from_time}
                                                                onChange={(e) => updateDay(day, "from_time", e.target.value)}
                                                                aria-label={t("teacherInfo.day_from", { day: dayLabels[day] })}
                                                            >
                                                                {TIME_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{t(opt.pm ? "teacherInfo.time_pm" : "teacherInfo.time_am", { time: opt.time })}</option>)}
                                                            </select>
                                                            <span className="tw-day-off" aria-hidden="true">–</span>
                                                            <select
                                                                className={`form-select ${invalid ? "is-invalid" : ""}`}
                                                                value={d.to_time}
                                                                onChange={(e) => updateDay(day, "to_time", e.target.value)}
                                                                aria-label={t("teacherInfo.day_to", { day: dayLabels[day] })}
                                                            >
                                                                {TIME_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{t(opt.pm ? "teacherInfo.time_pm" : "teacherInfo.time_am", { time: opt.time })}</option>)}
                                                            </select>
                                                        </div>
                                                    ) : (
                                                        <span className="tw-day-off">{t("teacherInfo.unavailable")}</span>
                                                    )}
                                                    {invalid && (
                                                        <p className="tw-error" role="alert">
                                                            <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
                                                            <span>{t("teacherInfo.end_after_start")}</span>
                                                        </p>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </Field>
                                </>
                            )}

                            {step === 5 && (
                                <>
                                    <p className="tw-card-desc">{t("teacherInfo.rate_desc")}</p>

                                    <Field id="hourly_rate" label={t("teacherInfo.hourly_rate")} error={err("hourly_rate")} hint={t("teacherInfo.hourly_rate_hint")}>
                                        <div className={`tw-input-group ${err("hourly_rate") ? "has-error" : ""}`}>
                                            <span className="tw-addon">$</span>
                                            <input
                                                id="hourly_rate" name="hourly_rate" type="number" min="0" step="0.5" inputMode="decimal"
                                                className={`form-control ${err("hourly_rate") ? "is-invalid" : ""}`}
                                                value={input.hourly_rate ?? ""}
                                                onChange={handleChange}
                                                placeholder="25"
                                                {...invalidProps("hourly_rate")}
                                            />
                                            <span className="tw-addon">{t("teacherInfo.usd_per_hour")}</span>
                                        </div>
                                    </Field>

                                    <h3 className="tw-section-title">
                                        <i className="fa-light fa-landmark" aria-hidden="true" /> {t("teacherInfo.payout_details")}
                                        <span className="tw-optional">({t("teacherInfo.optional").toLowerCase()})</span>
                                    </h3>
                                    <p className="tw-section-desc">{t("teacherInfo.bank_desc")}</p>

                                    <Field id="bank_name" label={t("teacherInfo.bank_name")} error={err("bank_name")}>
                                        <input
                                            id="bank_name" name="bank_name" type="text"
                                            className={`form-control ${err("bank_name") ? "is-invalid" : ""}`}
                                            value={input.bank_name ?? ""}
                                            onChange={(e) => { handleChange(e); clearError("account_number"); }}
                                            maxLength={255}
                                            {...invalidProps("bank_name")}
                                        />
                                    </Field>
                                    <Field id="account_number" label={t("teacherInfo.account_number")} error={err("account_number")} hint={t("teacherInfo.account_number_hint")}>
                                        <input
                                            id="account_number" name="account_number" type="text" autoComplete="off" spellCheck={false}
                                            className={`form-control ${err("account_number") ? "is-invalid" : ""}`}
                                            value={input.account_number ?? ""}
                                            onChange={(e) => { handleChange(e); clearError("bank_name"); }}
                                            maxLength={255}
                                            {...invalidProps("account_number")}
                                        />
                                    </Field>
                                    <Field id="bank_additional" label={t("teacherInfo.bank_additional")} optional error={err("bank_additional")}>
                                        <input
                                            id="bank_additional" name="bank_additional" type="text"
                                            className={`form-control ${err("bank_additional") ? "is-invalid" : ""}`}
                                            value={input.bank_additional ?? ""}
                                            onChange={handleChange}
                                            placeholder={t("teacherInfo.bank_additional_placeholder")}
                                            maxLength={1000}
                                            {...invalidProps("bank_additional")}
                                        />
                                    </Field>
                                </>
                            )}

                            {step === 6 && (
                                <>
                                    <p className="tw-card-desc">{t("teacherInfo.documents_desc", { size: DOC_MAX_MB })}</p>

                                    <div className="tw-doc-list">
                                        {DOCUMENTS.map((doc) => (
                                            <DocumentRow
                                                key={doc.key}
                                                doc={doc}
                                                file={docs[doc.key]}
                                                error={err(doc.key)}
                                                onFile={(f) => pickDocument(doc.key, f)}
                                                onClear={() => setDocs((prev) => ({ ...prev, [doc.key]: null }))}
                                            />
                                        ))}
                                    </div>
                                    <p className="tw-hint"><i className="fa-light fa-lock mt-1" aria-hidden="true" /> {t("teacherInfo.documents_private")}</p>

                                    <div className="tw-review">
                                        <div className="tw-review-head">{t("teacherInfo.review_title")}</div>
                                        {STEPS.slice(0, -1).map((s, i) => {
                                            const n = i + 1;
                                            const todo = Object.keys(validateStep(n)).length > 0;
                                            return (
                                                <button type="button" key={s.label} className={`tw-review-item ${todo ? "is-todo" : ""}`} onClick={() => { setBanner(null); setStep(n); setFurthest((f) => Math.max(f, n)); }}>
                                                    <i className={todo ? "fa-solid fa-circle-exclamation" : "fa-solid fa-circle-check"} aria-hidden="true" />
                                                    <span>{t(s.label)}</span>
                                                    <span className="tw-review-item-status">{todo ? t("teacherInfo.needs_attention") : t("teacherInfo.edit")}</span>
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {submitting && (
                                        <div className="tw-upload-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={uploadPct}>
                                            <span style={{ width: `${uploadPct}%` }} />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="tw-card-footer">
                            {step > 1 && (
                                <button type="button" className="tw-btn tw-btn-ghost" onClick={goBack} disabled={submitting}>
                                    <i className="fa-light fa-arrow-left tw-flip" aria-hidden="true" /> {t("teacherInfo.back")}
                                </button>
                            )}
                            <div className="tw-card-footer-end">
                                {step < STEPS.length ? (
                                    <button type="submit" className="tw-btn tw-btn-primary">
                                        {t("teacherInfo.continue")} <i className="fa-light fa-arrow-right tw-flip" aria-hidden="true" />
                                    </button>
                                ) : (
                                    <button type="submit" className="tw-btn tw-btn-primary" disabled={submitting}>
                                        {submitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                                {uploadPct > 0 && uploadPct < 100
                                                    ? t("teacherInfo.uploading", { pct: uploadPct })
                                                    : t("teacherInfo.submitting")}
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa-light fa-paper-plane-top tw-flip" aria-hidden="true" /> {t("teacherInfo.submit_for_review")}
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
